import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoJsonLd from "@/components/base/SeoJsonLd";
import { blogArticles } from "@/mocks/basetaoData";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://basetaosheet.surf";

export default function ShoppingGuide() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <>
      <SeoJsonLd
        type="Multi"
        items={[
          {
            type: "CollectionPage",
            data: {
              url: `${SITE_URL}/shopping-guide`,
              name: "BaseTao Spreadsheet Shopping Guide — All Articles & Tutorials",
              description: "Complete collection of BaseTao Spreadsheet guides, tutorials, reviews, and strategies covering shipping costs, warehouse management, coupon codes, product finding tips, and more.",
            },
          },
          {
            type: "BreadcrumbList",
            data: {
              items: [
                { name: "Home", url: SITE_URL },
                { name: "Shopping Guide", url: `${SITE_URL}/shopping-guide` },
              ],
            },
          },
        ]}
      />
      <Navbar />
      <main className="pt-20">
        <section className="w-full py-12 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 mb-4 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                  Back to Home
                </Link>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-3">
                  BaseTao Spreadsheet Shopping Guide
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                  Complete collection of BaseTao Spreadsheet guides, tutorials, reviews, and strategies. Everything you need to shop smarter, save money, and make informed purchasing decisions.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {blogArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/blog/${article.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
                    className="group block rounded-2xl border border-background-200/70 bg-background-100 overflow-hidden hover:border-primary-300 hover:shadow-md transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3 whitespace-nowrap">
                        {article.tag}
                      </span>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-4 text-xs text-primary-500 font-medium">
                        <span className="whitespace-nowrap">Read Article</span>
                        <i className="ri-arrow-right-line w-3 h-3 flex items-center justify-center"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="text-center mt-12 p-8 rounded-2xl bg-background-100 border border-background-200/70">
                <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-3">
                  Ready to Start Shopping?
                </h2>
                <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">
                  Browse thousands of products across all categories on the main BaseTao Spreadsheet platform.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    BaseTao Spreadsheet
                    <i className="ri-arrow-right-up-line ml-2 w-4 h-4 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-background-200 text-foreground-800 text-sm font-semibold hover:bg-background-300 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Back to Homepage
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}